import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { ANY_SPORT_SERIES_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import type { HexColor } from '@sorare/core/src/style/types';

import type { MissionCollectionCard_cardCollection } from './__generated__/index.graphql.ts';

const Root = styled(ButtonBase)`
  height: 100%;
  position: relative;
  border-radius: var(--double-unit);
  overflow: hidden;
  border: 1px solid var(--c-nd-150);
`;

const StyledNeonEdge = styled(NeonEdge)`
  padding: var(--quadruple-unit) var(--double-unit);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: var(--double-unit);
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const StyledCircularProgress = styled(CircularProgress)`
  width: var(--intermediate-unit);
`;

type Props = {
  cardCollection: MissionCollectionCard_cardCollection;
  strokeColor: string;
};

type CardCollectionWithUser = MissionCollectionCard_cardCollection & {
  userCardCollection?: { slotsCount?: number; fulfilledSlotsCount?: number };
};

export const MissionCollectionCard = ({
  cardCollection,
  strokeColor,
}: Props) => {
  const { team, userCardCollection } = cardCollection as CardCollectionWithUser;
  const { pictureUrl, name } = team || {};
  const { ownedPlayersCount, totalPlayersCount } = userCardCollection || {};
  const { generateSportContextPath } = useSportContext();
  const backgroundColor = ensureHexColor(team?.styleGuideColor);
  return (
    <Root
      to={generateSportContextPath(
        ANY_SPORT_SERIES_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
        {
          params: {
            collectionSlug: cardCollection.slug,
          },
        }
      )}
      className="text-center"
    >
      <StyledNeonEdge
        color={backgroundColor as HexColor}
        side="top"
        spread="40%"
        showHalo="always"
      >
        <StyledTextureBackground
          background="missions"
          color={backgroundColor as HexColor}
          imageOpacity={0.4}
        />

        {!!pictureUrl && <img alt={name ?? ''} src={pictureUrl} height={32} />}
        <Vertical center>
          <LabelS bold color="var(--c-white)">
            {name}
          </LabelS>
          <Horizontal center gap={0.5} style={{ alignItems: 'baseline' }}>
            <LabelM bold as="span" color="var(--c-white)">
              {ownedPlayersCount}
            </LabelM>
            <LabelS as="span" color="var(--c-white)">
              /{totalPlayersCount}
            </LabelS>
            <StyledCircularProgress
              style={
                { '--circular-progress-stroke': strokeColor } as CSSProperties
              }
              maxProgress={totalPlayersCount}
              progress={ownedPlayersCount}
            />
          </Horizontal>
        </Vertical>
      </StyledNeonEdge>
    </Root>
  );
};

MissionCollectionCard.fragments = {
  cardCollection: gql`
    fragment MissionCollectionCard_cardCollection on CardCollection {
      slug
      team: anyTeam {
        slug
        name
        pictureUrl
        styleGuideColor
      }
      userCardCollection(forUserSlug: $user) {
        id
        ownedPlayersCount
        totalPlayersCount
      }
    }
  ` as TypedDocumentNode<MissionCollectionCard_cardCollection>,
};
